"use client"

import { useState } from "react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Save, Building2, Mail, Phone, Globe, Users, Shield, Brain, Download } from "lucide-react"
import type { UserRole } from "@/lib/types"

const roles: UserRole[] = [
  "Department User",
  "Department Head",
  "AI Team",
  "SOA Reviewer",
  "Infra Reviewer",
  "Network Reviewer",
  "IT-Ops Reviewer",
  "Admin / Governance User",
]

const rolePermissions: Record<UserRole, string[]> = {
  "Department User": ["Submit pain points", "View own submissions"],
  "Department Head": ["Submit pain points", "View department use cases", "Approve departmental submissions"],
  "AI Team": ["Map pain points to use cases", "Edit AI mappings", "View all use cases"],
  "SOA Reviewer": ["Submit SOA feasibility reviews", "View all use cases"],
  "Infra Reviewer": ["Submit Infra feasibility reviews", "View all use cases"],
  "Network Reviewer": ["Submit Network feasibility reviews", "View all use cases"],
  "IT-Ops Reviewer": ["Submit IT-Ops feasibility reviews", "View all use cases"],
  "Admin / Governance User": [
    "Full access to all features",
    "Manage users and roles",
    "Export and report generation",
    "Approve for Discovery",
    "Reject or park use cases",
  ],
}

function ProfileSettings() {
  const [form, setForm] = useState({
    orgName: "National Commercial Bank",
    orgCode: "NCB",
    country: "Saudi Arabia",
    industry: "Banking & Financial Services",
    adminEmail: "ai-governance@ncb.com.sa",
    adminPhone: "+966 11 000 0000",
    website: "https://ncb.com.sa",
    description: "Leading financial institution managing AI use cases through a structured governance framework.",
  })

  return (
    <div className="flex flex-col gap-5 p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Building2 className="size-4" />
            Organisation Profile
          </CardTitle>
          <CardDescription className="text-xs">
            Organisation details displayed on documents and reports.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Organisation Name</label>
              <Input
                value={form.orgName}
                onChange={(e) => setForm((p) => ({ ...p, orgName: e.target.value }))}
                className="text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Organisation Code</label>
              <Input
                value={form.orgCode}
                onChange={(e) => setForm((p) => ({ ...p, orgCode: e.target.value }))}
                className="text-sm"
                placeholder="e.g. NCB, SAMA, ADIB"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Country</label>
              <Input
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
                className="text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Industry</label>
              <Select
                value={form.industry}
                onValueChange={(v) => setForm((p) => ({ ...p, industry: v }))}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Banking & Financial Services", "Insurance", "Asset Management", "FinTech", "Government", "Healthcare", "Retail"].map((i) => (
                    <SelectItem key={i} value={i} className="text-sm">{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                <Mail className="size-3 inline mr-1 text-muted-foreground" />
                Admin Email
              </label>
              <Input
                type="email"
                value={form.adminEmail}
                onChange={(e) => setForm((p) => ({ ...p, adminEmail: e.target.value }))}
                className="text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                <Phone className="size-3 inline mr-1 text-muted-foreground" />
                Admin Phone
              </label>
              <Input
                value={form.adminPhone}
                onChange={(e) => setForm((p) => ({ ...p, adminPhone: e.target.value }))}
                className="text-sm"
              />
            </div>
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                <Globe className="size-3 inline mr-1 text-muted-foreground" />
                Website
              </label>
              <Input
                value={form.website}
                onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
                className="text-sm"
              />
            </div>
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Description</label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="text-sm resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              className="gap-2"
              onClick={() => toast.success("Organisation profile saved.")}
            >
              <Save className="size-4" />
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function AISettings() {
  const [aiModel, setAiModel] = useState("Internal LLM (On-Premises)")
  const [tone, setTone] = useState("Professional")
  const [detailLevel, setDetailLevel] = useState("Standard")
  const [autoMap, setAutoMap] = useState("Manual Review Required")
  const [language, setLanguage] = useState("English")

  return (
    <div className="flex flex-col gap-5 p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Brain className="size-4" />
            AI Mapping Configuration
          </CardTitle>
          <CardDescription className="text-xs">
            Configure how the AI system maps pain points to use cases.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">AI Model</label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Internal LLM (On-Premises)", "OpenAI GPT-4", "Anthropic Claude", "Google Gemini", "Azure OpenAI"].map((m) => (
                    <SelectItem key={m} value={m} className="text-sm">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Currently configured: <span className="font-medium">{aiModel}</span></p>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Output Language</label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["English", "Arabic", "French"].map((l) => (
                    <SelectItem key={l} value={l} className="text-sm">{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Mapping Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Professional", "Technical", "Executive Summary", "Detailed"].map((t) => (
                    <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Detail Level</label>
              <Select value={detailLevel} onValueChange={setDetailLevel}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Brief", "Standard", "Comprehensive"].map((d) => (
                    <SelectItem key={d} value={d} className="text-sm">{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Auto-Mapping Approval Mode</label>
              <Select value={autoMap} onValueChange={setAutoMap}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Manual Review Required", "Auto-Approve Draft", "Auto-Submit to Feasibility"].map((a) => (
                    <SelectItem key={a} value={a} className="text-sm">{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Controls what happens after an AI mapping is generated.</p>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              className="gap-2"
              onClick={() => toast.success("AI settings saved.")}
            >
              <Save className="size-4" />
              Save AI Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Priority Scoring Formula</CardTitle>
          <CardDescription className="text-xs">Default weighting for the priority score calculation.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: "Business Impact", weight: "30%" },
              { label: "Risk / Compliance", weight: "20%" },
              { label: "Feasibility", weight: "20%" },
              { label: "Urgency", weight: "15%" },
              { label: "Reusability", weight: "10%" },
              { label: "Data Readiness", weight: "5%" },
            ].map(({ label, weight }) => (
              <div key={label} className="rounded-lg bg-muted/50 p-3 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{label}</span>
                <span className="text-sm font-semibold text-foreground">{weight}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Formula weights are pre-configured. Contact the AI Governance Team to adjust.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function RolesSettings() {
  const [selectedRole, setSelectedRole] = useState<UserRole>("AI Team")

  return (
    <div className="flex flex-col gap-5 p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="size-4" />
            Role Definitions
          </CardTitle>
          <CardDescription className="text-xs">
            Understand the permissions and responsibilities for each role in the system.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {roles.map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors
                  ${selectedRole === role
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:bg-muted"
                  }`}
              >
                {role}
              </button>
            ))}
          </div>

          <Separator />

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">{selectedRole}</h3>
              <Badge variant="secondary" className="text-xs">
                {rolePermissions[selectedRole].length} permissions
              </Badge>
            </div>
            <ul className="flex flex-col gap-2">
              {rolePermissions[selectedRole].map((perm) => (
                <li key={perm} className="flex items-center gap-2 text-sm text-foreground">
                  <span className="size-1.5 rounded-full bg-primary shrink-0" />
                  {perm}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="size-4" />
            Data Governance Principles
          </CardTitle>
          <CardDescription className="text-xs">Key governance rules enforced by this system.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-3">
            {[
              { title: "L1 Data Restriction", desc: "Use cases involving L1 (Confidential) data require mandatory review from all four feasibility teams before progressing." },
              { title: "Maker-Checker Workflow", desc: "All AI mapping outputs must be reviewed and approved by an AI Team member before entering the feasibility pipeline." },
              { title: "Audit Trail", desc: "All status changes, edits, and reviews are logged with timestamps and user information for full auditability." },
              { title: "Data Minimisation", desc: "AI solutions are scoped to access only the minimum data required to achieve the stated business objective." },
              { title: "Regulatory Alignment", desc: "AI use cases involving customer or regulatory data are flagged for compliance review before approval." },
            ].map(({ title, desc }) => (
              <li key={title} className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-foreground">{title}</span>
                <span className="text-xs text-muted-foreground">{desc}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

function ExportSettings() {
  const [format, setFormat] = useState("CSV")
  const [includeArchived, setIncludeArchived] = useState(false)

  return (
    <div className="flex flex-col gap-5 p-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Download className="size-4" />
            Export Configuration
          </CardTitle>
          <CardDescription className="text-xs">
            Configure default export format and options for reports.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Default Export Format</label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["CSV", "Excel (XLSX)", "PDF", "JSON"].map((f) => (
                    <SelectItem key={f} value={f} className="text-sm">{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Report Recipient Email</label>
              <Input
                type="email"
                placeholder="steering.committee@org.com"
                className="text-sm"
              />
            </div>
          </div>

          <Separator />

          <div className="flex flex-col gap-3">
            <span className="text-xs font-medium text-foreground">Default Export Sections</span>
            {[
              { label: "Use Case Register (all active use cases)", checked: true },
              { label: "High Priority Use Cases (P1 & P2)", checked: true },
              { label: "L1 Data Use Cases", checked: true },
              { label: "Feasibility Summary per Team", checked: true },
              { label: "Archived / Rejected Use Cases", checked: false },
              { label: "Priority Scoring Details", checked: false },
            ].map(({ label, checked }) => (
              <label key={label} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked={checked}
                  className="size-4 rounded border-border accent-primary"
                />
                <span className="text-sm text-foreground">{label}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              className="gap-2"
              onClick={() => toast.success("Export settings saved.")}
            >
              <Save className="size-4" />
              Save Export Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Quick Export</CardTitle>
          <CardDescription className="text-xs">Generate and download reports immediately.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: "Full Use Case Register", desc: "All use cases in CSV" },
              { label: "Steering Committee Report", desc: "Executive summary PDF" },
              { label: "Feasibility Status Report", desc: "All review team statuses" },
              { label: "Priority Ranking Report", desc: "Use cases ranked by score" },
            ].map(({ label, desc }) => (
              <div key={label} className="flex items-center justify-between rounded-lg border border-border p-3 gap-3">
                <div>
                  <p className="text-xs font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 shrink-0"
                  onClick={() => toast.success(`${label} export initiated.`)}
                >
                  <Download className="size-3" />
                  Export
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function SettingsContent() {
  return (
    <div className="p-0">
      <Tabs defaultValue="profile">
        <div className="border-b border-border px-6 pt-4">
          <TabsList className="h-9">
            <TabsTrigger value="profile" className="text-xs">Organisation Profile</TabsTrigger>
            <TabsTrigger value="ai" className="text-xs">AI Settings</TabsTrigger>
            <TabsTrigger value="roles" className="text-xs">Roles & Governance</TabsTrigger>
            <TabsTrigger value="export" className="text-xs">Export</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="profile" className="mt-0">
          <ProfileSettings />
        </TabsContent>
        <TabsContent value="ai" className="mt-0">
          <AISettings />
        </TabsContent>
        <TabsContent value="roles" className="mt-0">
          <RolesSettings />
        </TabsContent>
        <TabsContent value="export" className="mt-0">
          <ExportSettings />
        </TabsContent>
      </Tabs>
    </div>
  )
}
